import {
  removeCleanOrNotDefined,
  useCreateGcuPage,
  useCreateLegalNoticesPage,
  useCreatePrivacyPolicyPage,
  useGcuFormData,
  useLegalNoticesFormData,
  usePrivacyPolicyFormData,
  useUpdateGcuPage,
  useUpdateLegalNoticesPage,
  useUpdatePrivacyPolicyPage,
  wrapHandleSubmitWithAction,
} from "@/api/cms";
import {
  GcuForm,
  LegalNoticesForm,
  LegalNoticesFormProps,
  PrivacyPolicyForm,
} from "@/components/cms/PoliciesForm";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  constructLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout";
import {
  baseCmsFormSchema,
  gcuContentSchema,
  legalNoticesContentSchema,
  privacyPolicyContentSchema,
} from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { FormProvider, Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const legalNoticesSchema = multilingualFieldSchema(
  baseCmsFormSchema.merge(
    legalNoticesContentSchema.omit({
      id: true,
    }),
  ),
);

const gcuSchema = multilingualFieldSchema(
  baseCmsFormSchema.merge(
    baseCmsFormSchema.merge(gcuContentSchema.omit({ id: true })),
  ),
);

const privacyPolicySchema = multilingualFieldSchema(
  baseCmsFormSchema.merge(
    baseCmsFormSchema.merge(privacyPolicyContentSchema.omit({ id: true })),
  ),
);

const formSchema = z.object({
  legalNotices: legalNoticesSchema,
  gcu: gcuSchema,
  privacyPolicy: privacyPolicySchema,
});

type FormData = z.infer<typeof formSchema>;

const PoliciesCMS = () => {
  const { t } = useTranslation();

  const { data: legalNoticesData } = useLegalNoticesFormData();
  const { data: gcuData } = useGcuFormData();
  const { data: privacyPolicyData } = usePrivacyPolicyFormData();

  const createLegalNotices = useCreateLegalNoticesPage();
  const updateLegalNotices = useUpdateLegalNoticesPage();

  const createGcu = useCreateGcuPage();
  const updateGcu = useUpdateGcuPage();

  const createPrivacyPolicy = useCreatePrivacyPolicyPage();
  const updatePrivacyPolicy = useUpdatePrivacyPolicyPage();

  const { toast } = useToast();

  const defaultLegalNoticesValues = useMemo(
    () =>
      constructLocaleDict((lang) => {
        const legalNotices = legalNoticesData?.[lang];
        return {
          published: legalNotices?.published ?? false,
          pageName: legalNotices?.pageName ?? "",
          content: legalNotices?.content?.content,
        };
      }),
    [legalNoticesData],
  );

  const defaultGcuValues = useMemo(
    () =>
      constructLocaleDict((lang) => {
        const gcu = gcuData?.[lang];
        return {
          published: gcu?.published ?? false,
          pageName: gcu?.pageName ?? "",
          content: gcu?.content?.content,
        };
      }),
    [gcuData],
  );

  const defaultPrivacyPolicyValues = useMemo(
    () =>
      constructLocaleDict((lang) => {
        const privacyPolicy = privacyPolicyData?.[lang];
        return {
          published: privacyPolicy?.published ?? false,
          pageName: privacyPolicy?.pageName ?? "",
          content: privacyPolicy?.content?.content,
        };
      }),
    [privacyPolicyData],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      legalNotices: defaultLegalNoticesValues,
      gcu: defaultGcuValues,
      privacyPolicy: defaultPrivacyPolicyValues,
    },
  });

  useEffect(() => {
    form.reset({
      ...form.getValues(),
      legalNotices: defaultLegalNoticesValues,
    });
  }, [form, defaultLegalNoticesValues]);

  useEffect(() => {
    form.reset({
      ...form.getValues(),
      gcu: defaultGcuValues,
    });
  }, [form, defaultGcuValues]);

  useEffect(() => {
    form.reset({
      ...form.getValues(),
      privacyPolicy: defaultPrivacyPolicyValues,
    });
  }, [form, defaultPrivacyPolicyValues]);

  const onSubmit = (submittedData: FormData) => {
    Object.keys(submittedData.legalNotices).forEach((key) => {
      const localeData = submittedData.legalNotices[key as SupportedLocale];
      const baseData = legalNoticesData?.[key as SupportedLocale];
      if (
        !localeData ||
        !form.getFieldState(`legalNotices.${key}` as Path<FormData>).isDirty
      )
        return;

      const mutation = baseData?.content.id
        ? createLegalNotices
        : updateLegalNotices;

      const { pageName, published, ...content } = localeData;

      mutation.mutate({
        id: baseData?.content.id ?? "",
        lang: key as SupportedLocale,
        data: {
          pageName,
          published,
          content,
        },
      });
    });

    Object.keys(submittedData.privacyPolicy).forEach((key) => {
      const localeData = submittedData.privacyPolicy[key as SupportedLocale];
      const baseData = privacyPolicyData?.[key as SupportedLocale];
      if (
        !localeData ||
        !form.getFieldState(`privacyPolicy.${key}` as Path<FormData>).isDirty
      )
        return;

      const mutation = baseData?.content.id
        ? createPrivacyPolicy
        : updatePrivacyPolicy;

      const { pageName, published, ...content } = localeData;

      mutation.mutate({
        id: baseData?.content.id ?? "",
        lang: key as SupportedLocale,
        data: {
          pageName,
          published,
          content,
        },
      });
    });

    Object.keys(submittedData.gcu).forEach((key) => {
      const localeData = submittedData.gcu[key as SupportedLocale];
      const baseData = gcuData?.[key as SupportedLocale];
      if (
        !localeData ||
        !form.getFieldState(`gcu.${key}` as Path<FormData>).isDirty
      )
        return;

      const mutation = baseData?.content.id ? createGcu : updateGcu;

      const { pageName, published, ...content } = localeData;

      mutation.mutate({
        id: baseData?.content.id ?? "",
        lang: key as SupportedLocale,
        data: {
          pageName,
          published,
          content,
        },
      });
    });

    toast({
      title: t("success"),
      description: t("policiesPageSuccessMessage"),
      duration: 1000,
      variant: "success",
    });
  };

  const tabList = useMemo(
    () =>
      [
        {
          label: t("cms.policies.tabs.legalNotices"),
          component: LegalNoticesForm,
          value: "legal-notices",
          prepend: "legalNotices",
        },
        {
          label: t("cms.policies.tabs.gcu"),
          component: GcuForm,
          value: "gcu",
          prepend: "gcu",
        },
        {
          label: t("cms.policies.tabs.privacyPolicy"),
          component: PrivacyPolicyForm,
          value: "privacy-policy",
          prepend: "privacyPolicy",
        },
      ] satisfies {
        label: string;
        value: string;
        component: (props: LegalNoticesFormProps) => JSX.Element;
        prepend: string;
      }[],
    [t],
  );

  const cleanBeforeSubmit = () => {
    removeCleanOrNotDefined(form, legalNoticesData, "legalNotices");
    removeCleanOrNotDefined(form, gcuData, "gcu");
    removeCleanOrNotDefined(form, privacyPolicyData, "privacyPolicy");
  };

  return (
    <CMSLayout pageName={t("cmsTabPagesLegales")} formIds={["legal"]}>
      <FormProvider {...form}>
        <Tabs className="w-full" defaultValue="legal-notices">
          <form
            onSubmit={wrapHandleSubmitWithAction(
              form,
              cleanBeforeSubmit,
              onSubmit,
            )}
            id="legal"
            className="flex flex-col gap-4"
          >
            <TabsList>
              {tabList.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabList.map((tab) => {
              const Comp = tab.component;
              return (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className="inline-flex gap-8 w-full data-[state=inactive]:hidden"
                >
                  <DoubleMultilingualField
                    corner={(lang) => (
                      <Switch
                        dispLabel={false}
                        name={`${tab.prepend}.${lang}.published`}
                      />
                    )}
                    render={(lang) => (
                      <Comp formBase={`${tab.prepend}.${lang}`} />
                    )}
                  />
                </TabsContent>
              );
            })}
          </form>
        </Tabs>
      </FormProvider>
    </CMSLayout>
  );
};

export default PoliciesCMS;
